// Constants ported 1:1 from the Streamlit app.py interpretation layer.

export const YEAR_LABELS = ["FY 2022", "FY 2023", "FY 2024", "FY 2025", "FY 2026"];

export const INVESTOR_PROFILES: Record<string, string[]> = {
  "Quality Investor": [
    "Return on Equity", "Return on Capital Employed",
    "Cash Flow from Operations / Profit After Tax", "Free Cash Flow",
    "Operating Profit Margin", "Buffett Type Debt-to-Equity Ratio",
  ],
  "Conservative Investor": [
    "Buffett Type Debt-to-Equity Ratio", "Analytical Debt-to-Equity Ratio",
    "Free Cash Flow", "Cash Flow from Operations", "Working Capital Days",
  ],
  "Growth Investor": [
    "Earnings Per Share", "Reserves", "Operating Profit Margin", "Return on Capital Employed",
  ],
  "Value Investor": [
    "Free Cash Flow", "Cash Flow from Operations / Profit After Tax",
    "Buffett Type Debt-to-Equity Ratio", "Return on Capital Employed",
  ],
  "Dividend Investor": [
    "Free Cash Flow", "Cash Flow from Operations / Profit After Tax",
    "Buffett Type Debt-to-Equity Ratio", "Retained Earnings Percentage",
  ],
};

export const FILTER_TO_METRIC: Record<string, string> = {
  "Buffett Type Debt-to-Equity Ratio": "D/E Buffett Type Ratio",
  "Analytical Debt-to-Equity Ratio": "Debt to Equity Ratio (Analytical)",
  "Cash Flow from Operations": "Cash Flow from Operations",
  "Free Cash Flow": "Free Cash Flow",
  "Profit After Tax Margin": "Profit After Tax Margin",
  "Operating Profit Margin": "Operating Profit Margin",
  "Return on Equity": "Return on Equity",
  "Return on Capital Employed": "Return on Capital Employed",
  "Return on Assets": "Return on Assets",
  "Depreciation and Amortisation / Operating Profit": "Depreciation and Amortisation / Operating Profit",
  "Finance Cost / Operating Profit": "Finance Cost / Operating Profit",
  "Capital Expenditure / Profit After Tax": "Capital Expenditure / Profit After Tax",
  "Selling, General and Administrative Costs / Operating Profit": "Selling, General and Administrative Costs / Operating Profit",
  "Cash Flow from Operations / Profit After Tax": "Cash Flow from Operations / Profit After Tax",
  "Earnings Per Share": "Earnings Per Share",
  "Working Capital Days": "Working Capital Days",
  "Retained Earnings Percentage": "Retained Earnings Percentage",
  Reserves: "Reserves",
  "Inventory Turnover": "Inventory Turnover",
  "Inventory Days": "Inventory Days",
  "Debtor Days": "Debtor Days",
  "Days Payable": "Days Payable",
  "Cash Conversion Cycle": "Cash Conversion Cycle",
  "Equity Capital Change": "Equity Capital Change %",
  "Goodwill % of Total Assets": "Goodwill % of Total Assets",
};

export const LOWER_IS_BETTER = new Set<string>([
  "D/E Buffett Type Ratio",
  "Debt to Equity Ratio (Analytical)",
  "Inventory Days",
  "Debtor Days",
  // Days Payable deliberately excluded - not unambiguously "lower is
  // better" (see honey_core.py's LOWER_IS_BETTER for the full reasoning).
  "Cash Conversion Cycle",
  "Working Capital Days",
  "Selling, General and Administrative Costs / Operating Profit",
  "Depreciation and Amortisation / Operating Profit",
  "Finance Cost / Operating Profit",
  "Capital Expenditure / Profit After Tax",
  "Equity Capital Change %",
  "Goodwill % of Total Assets",
]);

export interface FilterExplanation {
  simple: string;
  why: string;
  reasons: string[];
  steps: string[];
}

export const FILTER_EXPLANATIONS: Record<string, FilterExplanation> = {
  "Cash Flow from Operations": {
    simple: "Cash generated from the core business itself, before investing or financing activity.",
    why: "Positive, growing CFO is the most direct evidence that reported profit is turning into real cash rather than sitting in receivables or inventory.",
    reasons: ["Receivables or inventory build-up", "One-off working-capital swing", "Weak underlying profitability", "Seasonal collection timing"],
    steps: ["Read the cash-flow statement", "Compare CFO growth with revenue growth", "Check receivables and inventory notes"],
  },
  "Cash Flow from Operations / Profit After Tax": {
    simple: "Cash profit should broadly track reported profit - not far below it, and not implausibly far above it either.",
    why: "A ratio well below 1x risks accounting earnings tied up in receivables or inventory; a ratio well above 1.5x is often a one-off (a working-capital release, a large advance received) rather than the durable norm - the target range is where cash and accounting profit genuinely agree.",
    reasons: ["Receivables increased", "Inventory absorbed cash", "Large one-off working-capital movement", "Unusually high CFO from a one-off advance or payable timing, not the sustainable run rate"],
    steps: ["Read the cash-flow statement", "Check receivables and inventory notes", "Compare CFO with PAT over several years"],
  },
  "Free Cash Flow": {
    simple: "The business should generate cash after funding necessary capital expenditure.",
    why: "Positive, improving free cash flow gives management more flexibility for debt reduction, dividends and reinvestment.",
    reasons: ["Heavy capital expenditure", "Weak operating cash flow", "Temporary expansion cycle"],
    steps: ["Separate maintenance and growth capex", "Inspect capex guidance", "Check whether new assets are producing returns"],
  },
  "Operating Profit Margin": {
    simple: "Operating margin shows how much operating profit remains from every ₹100 of revenue.",
    why: "Stable or rising margins can indicate pricing power, scale benefits or cost discipline.",
    reasons: ["Input-cost inflation", "Price competition", "Product-mix change", "Operating leverage"],
    steps: ["Read margin commentary", "Compare segment margins", "Check raw-material and employee costs"],
  },
  "Profit After Tax Margin": {
    simple: "PAT margin shows the final profit retained from revenue.",
    why: "It includes operating performance plus interest, depreciation, taxes and non-operating effects.",
    reasons: ["Higher finance cost", "Tax changes", "Exceptional items", "Weak operating margin"],
    steps: ["Reconcile operating profit to PAT", "Inspect exceptional items", "Check tax-rate changes"],
  },
  "Return on Capital Employed": {
    simple: "ROCE shows how efficiently the business earns on capital used.",
    why: "Durably high ROCE often signals efficient capital allocation and a stronger business model.",
    reasons: ["Under-utilised new capacity", "Low margins", "Acquisition drag", "Asset-heavy expansion"],
    steps: ["Check capital employed by segment", "Review asset utilisation", "Compare incremental profit with capex"],
  },
  "Return on Equity": {
    simple: "ROE shows the return earned on shareholders' capital.",
    why: "Strong ROE is useful only when it is not created mainly by excessive debt.",
    reasons: ["Margin changes", "Asset turnover changes", "Leverage", "Large idle reserves"],
    steps: ["Compare ROE with ROCE", "Check debt", "Review retained-earnings deployment"],
  },
  "Return on Assets": {
    simple: "ROA shows how much profit the business generates from everything it owns.",
    why: "Unlike ROE, ROA isn't inflated by debt - it measures how productively the whole asset base is used, regardless of how it's financed.",
    reasons: ["Asset-heavy business model", "Idle or underutilised assets", "Recent large capex not yet earning returns", "Margin pressure"],
    steps: ["Compare ROA with ROE to see how much debt is doing the work", "Check asset turnover", "Review recent capex and when it's expected to contribute"],
  },
  "Inventory Turnover": {
    simple: "Higher inventory turnover usually means inventory is moving faster.",
    why: "Slow-moving stock can trap cash, create write-down risk and weaken cash conversion.",
    reasons: ["Demand slowdown", "Deliberate stocking", "Product launch", "Obsolete inventory"],
    steps: ["Read inventory ageing", "Compare inventory growth with sales", "Check management commentary on stocking"],
  },
  "Inventory Days": {
    simple: "Inventory days estimate how long stock remains before sale.",
    why: "Rising days can point to slower demand or excess stock, though business models differ.",
    reasons: ["Demand slowdown", "Supply-chain buffer", "Seasonality", "Long production cycle"],
    steps: ["Compare with peers", "Review inventory ageing", "Check whether sales growth slowed"],
  },
  "Working Capital Days": {
    simple: "Working-capital days show how much cash is tied up in day-to-day operations.",
    why: "A rising requirement can consume cash even when profits grow.",
    reasons: ["Receivables stretched", "Inventory build-up", "Supplier terms changed"],
    steps: ["Inspect receivables, inventory and payables", "Read working-capital commentary", "Compare with peers"],
  },
  "Debtor Days": {
    simple: "Debtor days indicate how quickly customers pay.",
    why: "Rising debtor days can weaken cash flow and may indicate collection pressure.",
    reasons: ["Customer mix changed", "Loose credit terms", "Delayed government payments"],
    steps: ["Read receivables ageing", "Check bad-debt provisions", "Compare receivables growth with sales"],
  },
  "Days Payable": {
    simple: "Days payable show how long the company takes to pay its own suppliers.",
    why: "Unlike Inventory or Debtor Days, more isn't simply better or worse here - a longer period can mean the company is financing itself on supplier terms, but an unusually long or fast-rising one can also signal cash-flow stress delaying payments.",
    reasons: ["Deliberate supplier-financing strategy", "Genuine cash-flow stress", "Renegotiated supplier terms", "Change in purchasing mix"],
    steps: ["Compare with peers in the same industry", "Check supplier relationship commentary", "Review whether it's rising alongside other cash-stress signals"],
  },
  "Cash Conversion Cycle": {
    simple: "Cash Conversion Cycle (Inventory Days + Debtor Days - Days Payable) shows the full operating cash cycle, in days.",
    why: "A shorter (or negative) cycle means the business gets cash back faster than it pays suppliers - a genuine efficiency strength, not a data error when negative.",
    reasons: ["Inventory build-up", "Slower customer collections", "Shorter supplier credit terms", "Business-model or industry change"],
    steps: ["Break it down into its three components", "Check which component moved", "Compare the trend with peers"],
  },
  "Buffett Type Debt-to-Equity Ratio": {
    simple: "This ratio compares outside liabilities with net worth.",
    why: "Lower balance-sheet pressure gives a company more room during weak business cycles.",
    reasons: ["Debt-funded capex", "Acquisition", "Working-capital borrowing"],
    steps: ["Check repayment schedule", "Inspect interest coverage", "Review debt-funded projects"],
  },
  "Analytical Debt-to-Equity Ratio": {
    simple: "This ratio compares borrowings with shareholders' funds.",
    why: "High financial leverage can magnify both returns and risk.",
    reasons: ["Expansion borrowing", "Acquisition", "Weak internal cash generation"],
    steps: ["Check debt maturity", "Review finance cost", "Compare debt with CFO and FCF"],
  },
  "Capital Expenditure / Profit After Tax": {
    simple: "This shows how much profit is being absorbed by capital expenditure.",
    why: "Very high recurring capex can limit distributable free cash flow.",
    reasons: ["Capacity expansion", "Maintenance burden", "Regulatory spending"],
    steps: ["Separate growth and maintenance capex", "Check project commissioning", "Review expected asset returns"],
  },
  "Depreciation and Amortisation / Operating Profit": {
    simple: "This shows how much of operating profit is consumed by depreciation and amortisation.",
    why: "D&A is a non-cash charge, so a high ratio isn't a cash problem by itself - but it does signal an asset-heavy model that needs strong volume or margin growth to stay profitable as assets age.",
    reasons: ["Heavy recent capex", "Asset-intensive business model", "Accelerated depreciation policy", "Weak operating margin relative to asset base"],
    steps: ["Check the fixed-asset note for recent additions", "Compare with peers in the same industry", "Review depreciation policy in the accounting notes"],
  },
  "Finance Cost / Operating Profit": {
    simple: "This shows how much of operating profit is absorbed by interest and other finance costs.",
    why: "A rising share means debt is claiming more of the business's own earnings before a rupee reaches shareholders - the more of this ratio debt eats, the less room there is for a bad year.",
    reasons: ["Rising borrowings", "Higher interest rates", "Weaker operating profit", "Working-capital debt"],
    steps: ["Check total borrowings and their interest rates", "Review debt repayment schedule", "Compare finance cost trend with CFO"],
  },
  "Selling, General and Administrative Costs / Operating Profit": {
    simple: "This shows how much of operating profit is spent on employee costs, depreciation, finance cost and other overheads combined.",
    why: "A high or rising share means overheads are growing faster than the operating profit that has to fund them - a cost-discipline signal distinct from the gross-margin/COGS side of the business.",
    reasons: ["Employee cost growth outpacing revenue", "One-off or exceptional expenses", "Low-COGS business model where SGA is naturally the largest cost", "Weak operating leverage"],
    steps: ["Break SGA down into its components", "Compare growth in each component with revenue growth", "Check management commentary on cost control"],
  },
  Reserves: {
    simple: "Rising reserves suggest the business is retaining accumulated profits.",
    why: "The key question is whether retained money produces higher future earnings.",
    reasons: ["Profits retained", "Capital raised", "Accounting adjustments"],
    steps: ["Compare reserves growth with EPS", "Review acquisitions and capex", "Check return ratios"],
  },
  "Retained Earnings Percentage": {
    simple: "This shows what share of profit the business keeps and reinvests, rather than paying out as dividends.",
    why: "A high retention rate is only a strength if the retained capital earns a good return - pair this with ROE/ROCE rather than reading it alone.",
    reasons: ["Deliberate reinvestment-led growth strategy", "Temporarily reduced payout", "Building cash reserves for a specific project", "Regulatory or lender payout restrictions"],
    steps: ["Compare retained-earnings growth with ROE/ROCE", "Check dividend policy commentary", "Review how retained capital has been deployed historically"],
  },
  "Earnings Per Share": {
    simple: "EPS should ideally rise without excessive dilution.",
    why: "Per-share growth matters more to shareholders than total profit growth alone.",
    reasons: ["Profit growth", "Share issuance", "Buyback", "Exceptional items"],
    steps: ["Compare PAT and EPS growth", "Check share-count changes", "Review exceptional items"],
  },
  "Equity Capital Change": {
    simple: "A rise in equity capital may indicate dilution.",
    why: "New shares can reduce each existing shareholder's claim on future earnings.",
    reasons: ["Employee options", "Qualified placement", "Rights issue", "Acquisition consideration"],
    steps: ["Read share-capital notes", "Check promoter dilution", "Compare EPS with PAT growth"],
  },
  "Goodwill % of Total Assets": {
    simple: "Goodwill and intangibles should stay a small slice of the balance sheet.",
    why: "A large goodwill balance usually comes from paying a premium in past acquisitions - it carries impairment risk if those acquisitions underperform, and 0% is common and healthy for organically-grown businesses.",
    reasons: ["Recent acquisition at a premium", "Series of bolt-on acquisitions", "Brand or intangible-heavy business model", "Past impairment not yet written down"],
    steps: ["Check the intangible-assets note for what the goodwill relates to", "Review management commentary on acquisition integration", "Watch for impairment charges in future results"],
  },
};

export const MANUAL_FIELDS = [
  "revenue", "emp_exp", "fin_cost", "depr", "other_exp", "pbt", "pat", "eps",
  "div_pct", "eq_cap", "reserves", "borrowings", "total_liab", "fixed_assets",
  "total_assets", "cfo", "capex", "fcf_direct", "inv_days", "deb_days",
  "payable_days", "cash_conversion_cycle",
  "working_cap_days", "goodwill", "roe_direct", "roce_direct", "roa_direct",
];

export interface ManualFieldMeta {
  label: string;
  source: string;
  help: string;
}

export const MANUAL_FIELD_META: Record<string, ManualFieldMeta> = {
  revenue: { label: "Total Revenue", source: "Profit & Loss statement — Sales / Revenue row", help: "Main sales line used for margins and growth checks." },
  emp_exp: { label: "Employee Benefit Expense", source: "P&L expense breakup — Employee Cost", help: "Part of SGA cost-discipline ratios." },
  fin_cost: { label: "Finance Cost", source: "P&L expense breakup — Interest", help: "Interest burden vs operating profit." },
  depr: { label: "Depreciation and Amortisation", source: "P&L — Depreciation row", help: "Depreciation burden vs operating profit." },
  other_exp: { label: "Other Expenses", source: "P&L expense breakup — Other Expenses", help: "Part of SGA cost-discipline ratios." },
  pbt: { label: "Profit Before Tax", source: "P&L — Profit before tax row", help: "Used in ROCE fallback formula." },
  pat: { label: "Profit After Tax", source: "P&L — Net Profit row", help: "Core profitability input for most ratios." },
  eps: { label: "Earnings Per Share", source: "P&L — EPS in Rs row", help: "Per-share growth and dilution checks." },
  div_pct: { label: "Dividend Payout Percentage", source: "P&L — Dividend Payout % row", help: "Used for the retained-earnings percentage." },
  eq_cap: { label: "Equity Share Capital", source: "Balance Sheet — Equity Capital row", help: "Dilution detection via capital changes." },
  reserves: { label: "Reserves", source: "Balance Sheet — Reserves row", help: "Retained-earnings proxy; rising trend preferred." },
  borrowings: { label: "Borrowings", source: "Balance Sheet — Borrowings row", help: "Analytical debt-to-equity ratio." },
  total_liab: { label: "Total Liabilities", source: "Balance Sheet — Total Liabilities row", help: "Buffett-type D/E uses outside liabilities." },
  fixed_assets: { label: "Fixed Assets", source: "Balance Sheet — Net Block / Fixed Assets", help: "Asset-intensity context." },
  total_assets: { label: "Total Assets", source: "Balance Sheet — Total Assets row", help: "Return on Assets calculation." },
  cfo: { label: "Cash Flow from Operations", source: "Cash Flow statement — Cash from Operating Activity", help: "Cash conversion quality (CFO/PAT)." },
  capex: { label: "Capital Expenditure", source: "Cash Flow — Fixed assets purchased (investing)", help: "CapEx/PAT and Free Cash Flow." },
  fcf_direct: { label: "Free Cash Flow", source: "CFO minus CapEx (or direct value if published)", help: "Cash left after necessary investment." },
  inv_days: { label: "Inventory Days", source: "Ratios section — Inventory Days", help: "Inventory turnover = 365 / Inventory Days." },
  deb_days: { label: "Debtor Days", source: "Ratios section — Debtor Days", help: "Speed of customer collections." },
  payable_days: { label: "Days Payable", source: "Ratios section — Days Payable", help: "How long the company takes to pay its own suppliers." },
  cash_conversion_cycle: { label: "Cash Conversion Cycle", source: "Ratios section — Cash Conversion Cycle", help: "Inventory Days + Debtor Days - Days Payable, if left blank." },
  working_cap_days: { label: "Working Capital Days", source: "Ratios section — Working Capital Days", help: "Cash tied in day-to-day operations." },
  goodwill: { label: "Goodwill and Intangible Assets", source: "Balance Sheet notes — Intangibles", help: "Acquisition-heavy balance sheet check." },
  roe_direct: { label: "Return on Equity", source: "Ratios section — ROE %", help: "Direct value preferred over formula fallback." },
  roce_direct: { label: "Return on Capital Employed", source: "Ratios section — ROCE %", help: "Direct value preferred over formula fallback." },
  roa_direct: { label: "Return on Assets", source: "Ratios section — ROA % (if published)", help: "Asset productivity measure." },
};

export const CHART_COLORS: Record<string, string> = {
  Revenue: "#3B73E8",
  "Profit After Tax": "#1E9E58",
  "Cash Flow from Operations": "#B98900",
  "Free Cash Flow": "#E9800B",
  "Return on Equity": "#2764E7",
  "Return on Capital Employed": "#119B55",
  "D/E Buffett Type Ratio": "#DC4437",
  "Cash Flow from Operations / Profit After Tax": "#B98900",
  "Free Cash Flow / Profit After Tax": "#E9800B",
  "Operating Profit Margin": "#B98900",
  "Profit After Tax Margin": "#DC7B0B",
  "Inventory Turnover": "#0F9488",
  "Inventory Days": "#DC4437",
  Reserves: "#0EA5A6",
  "Earnings Per Share": "#8A5A00",
};

export const FALLBACK_COLORS = ["#3B73E8", "#1E9E58", "#B98900", "#E9800B", "#DC4437", "#0EA5A6", "#8A5A00"];

export const QUALITY_PULSE_FILTERS: [string, string][] = [
  ["Operating Profit Margin", "Operating quality"],
  ["Working Capital Days", "Operating quality"],
  ["Cash Conversion Cycle", "Operating quality"],
  ["Inventory Turnover", "Operating quality"],
  ["Selling, General and Administrative Costs / Operating Profit", "Cost discipline"],
  ["Capital Expenditure / Profit After Tax", "Cost discipline"],
  ["Retained Earnings Percentage", "Capital allocation"],
  ["Equity Capital Change", "Capital allocation"],
];
